import qs from 'qs'
import * as performanceMonitor from 'loca-monitor'
import { autoTrackClickEvent, getPerformanceTiming } from './trackEventUtils'
import { WEBPERFORMANCE_CORE_SCENE } from './config'
import sensors, { initSensors } from './sensors'
import { startMonitorResources } from './resourceMonitor'

// 是否已初始化
let hasInited = false

// 是否已上报过性能
let hasTrackPerformance = false

// 是否已上报过核心场景性能
const coreMap: any = {}

export interface PerformanceMonitorOption {
  coreSceneMap: Record<string, string>
  defaultPath?: string
  prefix?: string
  logger?: (event: string, info: any) => void
}

// eslint-disable-next-line prefer-const
let { isf, hsr, flt, hsl, wsl }: any = qs.parse(window.location.search, { ignoreQueryPrefix: true })
const originPath = window.location.pathname


export interface InitOptions {
  // 是否开启 xhr 请求监控
  enableXHRMonitor?: boolean
}

const Analytics = {
  // 初始化埋点
  init(options?: InitOptions) {
    if (!hasInited) {
      // 仅初始化一次的标识位
      hasInited = true

      // 全局监听页面所有需要触发埋点上报的元素点击事件
      autoTrackClickEvent(this.track)

      // 初始化神策埋点
      // todo 区分国内外使用不同的埋点库
      initSensors()

      // 初始化性能埋点
      this.performanceMonitor.init({ coreSceneMap: {} })

      // 开启全局 xhr 请求监听
      if (options?.enableXHRMonitor) {
        startMonitorResources(this.track)
      }
    }
  },

  // 绑定 uv 的 id
  login(id: string) {
    // todo 区分国内外使用不同的埋点库
    sensors.login(id)
  },

  // 退出 uv id 的绑定
  logout() {
    // todo 区分国内外使用不同的埋点库
    sensors.logout()
  },

  // 手动上报 pv
  trackPageView() {
    this.track('$pageview')

    // 性能上报
    this.trackPerformance()
  },

  // 手动上报自定义事件
  track(event: string, properties?: Record<string, boolean | string | number>) {
    // todo 区分国内外，使用不同的埋点库
    sensors.track(event, properties)
  },

  // 性能上报
  trackPerformance() {
    // 仅首次打开页面时进行性能上报
    if (!hasTrackPerformance) {
      hasTrackPerformance = true

      // 延迟 1秒 上报，确保大部分情况下已正确加载
      setTimeout(() => {
        const timing = getPerformanceTiming()
        this.track('WebPerformance', timing)
      }, 1000)
    }
  },
  // 支持扩展此函数，添加更多的中间埋点节点。
  trackCoreScene(type: string) {
    try {
      // 记录过，不重复记录
      if (coreMap[type]) return

      // 若触发上报时的 path 与首次进入时的 path 不一致，则不做记录
      const pathname = window && window.location.pathname
      if (pathname !== originPath) return

      // 上报的数据结构
      const logData: any = { type }
      // 通用耗时
      if (hsl) {
        logData.time_common = Date.now() - hsl
      } else {
        performance.mark(`${type}_end`)
        // 以navigationStart作为开始事件，记录通用耗时
        // performance.measure ipad safari 不兼容
        logData.time_common = performance.measure(type, 'navigationStart', `${type}_end`).duration
      }
      // home login 耗时: home start redirect - home start login
      if (hsr && hsl) logData.time_home = hsr - hsl
      // webview 耗时: web start load - home start redirect
      if (wsl && hsr) logData.time_webview = wsl - hsr
      // 首次加载只有首次记录
      // app start create
      // 小程序首次加载耗时: home start login - home start createPage
      if (+isf === 1 && flt) logData.time_load = flt

      // 上报数据
      this.track(WEBPERFORMANCE_CORE_SCENE, logData)
    } catch (error) {
      console.error(error)
    }
  },

  // 独立的核心性能监控 sdk
  performanceMonitor: {
    MFE_START_TIME_NAME: 'pg_core_mfe_start_time',
    MFE_DURATION_START_TIME_NAME: 'pg_core_duration_mfe_start_time',

    timeSliceStarTime: null as any,
    timeSliceStarTimeMap: {} as any,

    init(options: PerformanceMonitorOption) {
      performance.mark(this.MFE_START_TIME_NAME)

      performanceMonitor.initMonitorSettings({
        prefix: 'pg',
        logger: Analytics.track,
        defaultPath: '',
        ...options
      })
    },

    // 同样的 type 只会成功触发一次上报
    trackCoreSceneOnce(type: string) {
      performanceMonitor.markCoreScene(type, { startName: this.MFE_START_TIME_NAME })
    },

    // 指定时间段埋点 - 标记开始时间
    startTimeSliceTrack(type?: string) {
      if (type) {
        this.timeSliceStarTimeMap[type] = Date.now()
      } else {
        this.timeSliceStarTime = Date.now()
      }
    },

    /**
     * 指定时间段埋点 - 以当前时间上报时间段结果
     * @param type 核心场景类型
     * @param code 这里利用 tenant_code 做 type 的细化分类
     */
    endTimeSliceTrack(type: string, code = '') {
      const startTime = this.timeSliceStarTimeMap[type] || this.timeSliceStarTime
      if (!startTime) return

      const time_common = Date.now() - startTime
      Analytics.track('webPerformanceCoreScene', { type, time_common, tenant_code: code })

      if (this.timeSliceStarTimeMap[type]) {
        this.timeSliceStarTimeMap[type] = null
      } else {
        this.timeSliceStarTime = null
      }

    }
  }
}

export default Analytics
